import React, { useCallback, useMemo, useState } from 'react';

import { debounce } from 'lodash';

import { Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import Flag from '~/components/Common/Flag';

import { AVAILABLE_REGIONS } from '~/consts/agentHub';

import { useListEoi } from '~/hooks/apis/agent/useListEoi';
import useAppNavigation from '~/hooks/useAppNavigation';
import useQuery from '~/hooks/useQuery';

import { Eoi, EoiRegionCode, EoiStatus, ListEoiParams } from '~/types/services/agentHub';

import EoiStatusChip from '../EoiStatusChip';

import AgencySubmissionDetail from './AgencySubmissionDetails/AgencySubmissionDetailModal';
import AgencySubmissionToggleOnboardModal from './AgencySubmissionDetails/AgencySubmissionToggleOnboardModal';
import CreateUserSubmissionModal from './CreateUserSubmissionModal';

const DEFAULT_SIZE_PER_PAGE = 10;

const ALLOWED_USER_STATUSES_TOGGLE = [EoiStatus.Onboarded, EoiStatus.Offboarded];

export default function AgencySubmissions() {
  const queryFilters = useQuery();

  const { push: setQueryString } = useAppNavigation();

  const [isOffboardModalOpen, setIsOffboardModalOpen] = useState(false);
  const [toggleOnboardUser, setToggleOnboardUser] = useState<Eoi | undefined>(undefined);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const filter = useMemo(() => {
    return {
      page: parseInt(queryFilters.get('page') ?? '0'),
      status: queryFilters.get('status'),
      regionCode: queryFilters.get('region'),
      search: queryFilters.get('search') ?? undefined,
      id: queryFilters.get('id') ?? undefined,
    };
  }, [queryFilters]);

  const {
    data: { rows: result, total },
    isFetching,
    refetch: refetchEois,
  } = useListEoi({
    page: filter.page.toString(),
    pageSize: DEFAULT_SIZE_PER_PAGE.toString(),
    status: filter.status as ListEoiParams['status'],
    regionCode: filter.regionCode as EoiRegionCode,
    search: filter.search,
  });

  const onPageChange = useCallback(
    async (page: number): Promise<void> => {
      queryFilters.set('page', page.toString());
      setQueryString({
        search: queryFilters.toString(),
      });
    },
    [queryFilters, setQueryString],
  );

  const onStatusChange = useCallback(
    (status: string) => {
      queryFilters.set('page', '0');
      if (!status || status === 'all') {
        queryFilters.delete('status');
      } else {
        queryFilters.set('status', status);
      }
      setQueryString({
        search: queryFilters.toString(),
      });
    },
    [queryFilters, setQueryString],
  );

  const onSearchChange = useCallback(
    (search: string) => {
      queryFilters.set('page', '0');
      if (search === '') {
        queryFilters.delete('search');
      } else {
        queryFilters.set('search', search);
      }
      setQueryString({
        search: queryFilters.toString(),
      });
    },
    [queryFilters, setQueryString],
  );

  const onRegionChange = useCallback(
    (region: string) => {
      queryFilters.set('page', '0');
      if (!region || region === 'all') {
        queryFilters.delete('region');
      } else {
        queryFilters.set('region', region);
      }
      setQueryString({
        search: queryFilters.toString(),
      });
    },
    [queryFilters, setQueryString],
  );

  const debouncedOnSearchChange = useMemo(
    () =>
      debounce((search: string) => {
        onSearchChange(search);
      }, 500),
    [onSearchChange],
  );

  const closeDetailsModal = useCallback(() => {
    queryFilters.delete('id');
    setQueryString({
      search: queryFilters.toString(),
    });
  }, [queryFilters, setQueryString]);

  const handleOpenCreateModal = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const handleCreateSuccess = useCallback(() => {
    refetchEois();
  }, [refetchEois]);

  const openDetailsModal = useCallback(
    (id: string) => {
      queryFilters.set('id', id);
      setQueryString({
        search: queryFilters.toString(),
      });
    },
    [setQueryString, queryFilters],
  );

  const closeOffboardModal = useCallback(() => {
    setIsOffboardModalOpen(false);
  }, [setIsOffboardModalOpen]);

  const openToggleOnboardStatusModal = useCallback(
    (user: Eoi) => {
      setToggleOnboardUser(user);
      setIsOffboardModalOpen(true);
    },
    [setToggleOnboardUser, setIsOffboardModalOpen],
  );

  const columns: Array<GridColDef> = useMemo(() => {
    return [
      { field: 'agencyName', headerName: 'Agency Name', flex: 1, sortable: false, display: 'flex' },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        sortable: false,
        display: 'flex',
        renderCell: (params) => <Typography>{`${params.row.givenName} ${params.row.surname}`}</Typography>,
      },
      {
        field: 'status',
        headerName: 'Status',
        align: 'center',
        flex: 1,
        sortable: true,
        display: 'flex',
        renderCell: (params) => <EoiStatusChip status={params.row.status} />,
      },
      {
        field: 'userStatus',
        headerName: 'User Status',
        align: 'center',
        flex: 1,
        sortable: true,
        display: 'flex',
        renderCell: (params) => (params.row.userStatus ? <EoiStatusChip status={params.row.userStatus} /> : '-'),
      },
      { field: 'submittedAt', headerName: 'Submitted At', flex: 1, sortable: true, display: 'flex' },
      { field: 'updatedAt', headerName: 'Updated At', flex: 1, sortable: true, display: 'flex' },
      {
        field: 'user',
        headerName: 'Country',
        flex: 1,
        display: 'flex',
        align: 'center',
        renderCell: (params) => {
          return params.row.regionCode && <Flag region={params.row.regionCode} style={{ marginRight: 10 }} />;
        },
      },
      {
        field: 'stateOrTerritory',
        headerName: 'State',
        flex: 1,
        sortable: true,
        display: 'flex',
        renderCell: (params) => <Typography>{params.row.stateOrTerritory || '-'}</Typography>,
      },
      {
        field: 'action',
        headerName: '',
        flex: 1,
        renderCell: (params) => {
          return (
            <>
              <Button variant="text" onClick={() => openDetailsModal(params.row.id)}>
                Show
              </Button>
              <Button
                disabled={!ALLOWED_USER_STATUSES_TOGGLE.includes(params.row.userStatus) || !params.row.customerId}
                variant="text"
                onClick={() => openToggleOnboardStatusModal(params.row)}
              >
                {params.row.userStatus === EoiStatus.Offboarded ? 'Onboard' : 'Offboard'}
              </Button>
            </>
          );
        },
        display: 'flex',
      },
    ];
  }, [openDetailsModal, openToggleOnboardStatusModal]);

  return (
    <>
      <Stack gap={2} display="flex" justifyContent="end">
        <Stack direction="row" gap={1}>
          <TextField
            sx={{ flex: 1 }}
            data-testid="search"
            type="text"
            name="search"
            label="Search"
            placeholder="Search"
            defaultValue={filter.search}
            onChange={(e) => debouncedOnSearchChange(e.target.value)}
          />
          <TextField
            sx={{ minWidth: 130 }}
            select
            title="Status"
            label="Status"
            value={filter.status}
            onChange={(e) => onStatusChange(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="onboarded">Onboarded</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </TextField>
          <TextField
            sx={{ minWidth: 130 }}
            select
            title="Country"
            label="Country"
            value={filter.regionCode}
            onChange={(e) => onRegionChange(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            {AVAILABLE_REGIONS.map((region) => (
              <MenuItem key={region.value} value={region.value}>
                {region.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Button sx={{ flex: 1, alignSelf: 'end' }} variant="contained" onClick={handleOpenCreateModal}>
          Create User
        </Button>
      </Stack>
      <Stack mt={1}>
        <DataGrid
          rows={result}
          rowCount={total}
          columns={columns}
          loading={isFetching}
          pagination
          paginationMode="server"
          paginationModel={{ page: filter.page, pageSize: DEFAULT_SIZE_PER_PAGE }}
          pageSizeOptions={[DEFAULT_SIZE_PER_PAGE]}
          onPaginationModelChange={({ page }) => onPageChange(page)}
          slots={{ pagination: GridPagination }}
          disableColumnFilter
          disableColumnMenu
          disableRowSelectionOnClick
          autoHeight
        />
        {filter.id && <AgencySubmissionDetail onClose={closeDetailsModal} id={filter.id} onUpdate={refetchEois} />}
        <AgencySubmissionToggleOnboardModal
          open={isOffboardModalOpen}
          onClose={closeOffboardModal}
          user={toggleOnboardUser}
          onUpdate={refetchEois}
        />
        <CreateUserSubmissionModal
          isOpen={isCreateModalOpen}
          onClose={handleCloseCreateModal}
          onSuccess={handleCreateSuccess}
        />
      </Stack>
    </>
  );
}
